import { colorFields } from "./_shared";
/**
 * About section — for sellers to tell their store's story alongside an
 * optional image. Renders as a 2-column layout on desktop, stacked on
 * mobile. Plays double duty: builds buyer trust (PROJECT_CONTEXT §16
 * trust signals) and adds unique long-form content per store for SEO.
 */
export const aboutSchema = {
    type: "about",
    name: { ar: "من نحن", en: "About" },
    description: {
        ar: "قصة المتجر بجانب صورة اختيارية. عمودين على الشاشات الكبيرة.",
        en: "The store's story alongside an optional image. Two columns on desktop.",
    },
    maxPerTemplate: 2,
    settings: [
        {
            id: "variant",
            type: "select",
            ui: "thumbnail",
            label: { ar: "نوع العرض", en: "Layout" },
            helpText: {
                ar: "طريقة عرض قصة المتجر.",
                en: "How the story is laid out.",
            },
            options: [
                { value: "about-split", label: { ar: "عمودان", en: "Split" } },
                { value: "about-stacked", label: { ar: "متوسط", en: "Stacked" } },
                { value: "about-overlay", label: { ar: "تراكب", en: "Overlay" } },
                { value: "about-minimal", label: { ar: "بسيط", en: "Minimal" } },
                { value: "about-card", label: { ar: "بطاقة", en: "Card" } },
                // Ported from EasyOrders `editorial-feature` (elite family). Same
                // content model as `about-split` — eyebrow + title + body beside an
                // image — with a serif display title, a 4:5 portrait plate and an
                // arrow link. A variant, not a new section, for exactly the reason
                // the compare panel became a `before-after` variant.
                { value: "about-editorial", label: { ar: "تحريري", en: "Editorial" } },
                // Ported from EasyOrders `ps-split-duo-hero` (nature family,
                // 2026-07-20). A rounded two-tone panel: centred kicker + title +
                // body + pill CTA on one half, a full-bleed photograph on the other.
                // The content model is exactly `about-editorial`'s — eyebrow, title,
                // body, image, ctaLabel, ctaHref — so it is a variant for the same
                // reason the compare panel became a `before-after` variant.
                { value: "about-duo", label: { ar: "لوحة مزدوجة", en: "Duo panel" } },
            ],
            default: "about-split",
        },
        {
            id: "eyebrow",
            type: "text",
            i18n: true,
            label: { ar: "نص صغير فوق العنوان (اختياري)", en: "Eyebrow (optional)" },
            helpText: {
                ar: 'نص صغير فوق العنوان، مثل "قصتنا".',
                en: 'A small label above the title, e.g. "Our story".',
            },
            validate: { max: 30 },
        },
        {
            id: "title",
            type: "text",
            i18n: true,
            label: { ar: "العنوان", en: "Title" },
            validate: { max: 60 },
            default: { ar: "من نحن", en: "About us" },
        },
        {
            id: "body",
            type: "textarea",
            i18n: true,
            label: { ar: "النص", en: "Body" },
            helpText: {
                ar: "احكي عن متجرك. إيه اللي بيميزه؟",
                en: "Tell us about your store. What makes it special?",
            },
            validate: { max: 600 },
        },
        {
            id: "image",
            type: "image",
            label: { ar: "الصورة", en: "Image" },
            helpText: {
                ar: "صورة من وراء الكواليس، لقطة قريبة للمنتج، أو صورة الفريق.",
                en: "Behind-the-scenes shot, product close-up, or team photo.",
            },
            focalPoint: true,
            // A11: the minimal variant renders no image box, so hide the image control
            // there (every other variant shows one).
            showIf: {
                variant: [
                    "about-split",
                    "about-stacked",
                    "about-overlay",
                    "about-card",
                    "about-editorial",
                    "about-duo",
                ],
            },
        },
        {
            id: "imagePosition",
            type: "select",
            label: { ar: "موضع الصورة", en: "Image position" },
            options: [
                { value: "start", label: { ar: "البداية", en: "Start" } },
                { value: "end", label: { ar: "النهاية", en: "End" } },
            ],
            default: "end",
            // A2: only the split + card layouts place the image beside the text, so
            // "image position" is a no-op for stacked / overlay / minimal.
            showIf: {
                variant: ["about-split", "about-card", "about-editorial", "about-duo"],
            },
        },
        {
            id: "verticalAlign",
            type: "select",
            label: { ar: "محاذاة النص (فوق/وسط)", en: "Text vertical align" },
            helpText: {
                ar: "مكان النص جنب الصورة: من فوق ولا في النص.",
                en: "Where the text sits next to the image: top or centered.",
            },
            options: [
                { value: "top", label: { ar: "من الأعلى", en: "Top" } },
                { value: "center", label: { ar: "المنتصف", en: "Center" } },
            ],
            default: "top",
            showIf: { variant: ["about-split", "about-card"] },
        },
        // The editorial variant's arrow link. Their markup ships it as an optional
        // `editorial-feature__link`; scoped to that variant so the other four
        // layouts do not sprout a control they never render.
        {
            id: "ctaLabel",
            type: "text",
            i18n: true,
            label: { ar: "نص الرابط (اختياري)", en: "Link label (optional)" },
            validate: { max: 40 },
            showIf: { variant: ["about-editorial", "about-duo"] },
        },
        {
            id: "ctaHref",
            type: "url",
            label: { ar: "رابط الزر", en: "Link destination" },
            helpText: {
                ar: "لينك الصفحة اللي الرابط يودّي لها.",
                en: "Where the link goes.",
            },
            showIf: { variant: ["about-editorial", "about-duo"] },
        },
        {
            id: "headingLevel",
            type: "headingLevel",
            label: { ar: "مستوى العنوان", en: "Heading level" },
            default: 2,
        },
        ...colorFields(),
    ],
};
//# sourceMappingURL=about.js.map